// Posts Feed Web Component
import ProtectedComponent from './protected-component.js';
import { isVisible, observeVisibility } from '../utils/component-visibility.js';
import { focusGroupFeedHash } from '../utils/group-feed-hash.js';

class PostsFeed extends ProtectedComponent {
  constructor() {
    super();
    this.posts = [];
    this.isLoading = false;
    this.isLoadingMore = false;
    this.error = null;
    this.currentUser = null;
    this.currentPage = 1;
    this.pagination = null;
    this.isLoadingPosts = false; // Prevent multiple simultaneous calls
    this.visibilityObserver = null;
    this.hasLoadedOnce = false; // Track if we've loaded data at least once

    // Ensure posts is always an array
    if (!this.posts) {
      this.posts = [];
    }
  }

  static get observedAttributes() {
    return ['group-id', 'thread-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected && (name === 'group-id' || name === 'thread-id')) {
      this.hasLoadedOnce = false;
      this.currentPage = 1;
      this.loadIfVisible();
    }
  }

  get groupId() {
    const raw = this.getAttribute('group-id');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  get threadId() {
    const raw = this.getAttribute('thread-id');
    if (!raw) return null;
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  isGroupMode() {
    return this.groupId !== null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.attachDocumentListeners();

    // Read page from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (!isNaN(page) && page > 0) {
        this.currentPage = page;
      }
    }

    const initialPageAttr = this.getAttribute('data-initial-page');
    if (initialPageAttr) {
      const page = parseInt(initialPageAttr, 10);
      if (!isNaN(page) && page > 0) {
        this.currentPage = page;
      }
    }

    // Only load if visible, otherwise wait for visibility
    this.loadIfVisible();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up visibility observer
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
  }

  /**
   * Load data only if component is visible, otherwise observe for visibility
   */
  loadIfVisible() {
    if (isVisible(this)) {
      this.load();
      this.hasLoadedOnce = true;
    } else {
      // Component is hidden, wait for it to become visible
      this.visibilityObserver = observeVisibility(this, () => {
        if (!this.hasLoadedOnce) {
          this.load();
          this.hasLoadedOnce = true;
        }
      });
    }
  }

  renderHeaderActions() {
    if (this.isGroupMode()) {
      return `
        <button class="btn btn-secondary refresh-btn">
          <span class="refresh-icon">🔄</span>
          Refresh
        </button>
      `;
    }

    return `
      <div class="feed-header-actions">
        <button class="btn btn-secondary refresh-btn">
          <span class="refresh-icon">🔄</span>
          Refresh
        </button>
        <toolbar-overflow-actions>
          <button slot="overflow" class="btn btn-secondary" id="openFeedFiltersBtn">🔍 Filter</button>
        </toolbar-overflow-actions>
      </div>
    `;
  }

  render() {
    const feedTitle = this.isGroupMode() ? 'Group Posts' : 'Recent Posts';
    this.innerHTML = `
      <div class="posts-feed">
        <div class="feed-header">
          <h2>${feedTitle}</h2>
          ${this.renderHeaderActions()}
        </div>

        <div class="feed-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
  }

  openFeedFiltersModal() {
    let modal = document.querySelector('feed-filters-modal');
    if (!modal) {
      modal = document.createElement('feed-filters-modal');
      document.body.appendChild(modal);
    }
    modal.open();
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading posts...</div>
        </div>
      `;
    }

    if (this.error) {
      return `
        <div class="error-state">
          <div class="error-message">❌ ${this.error}</div>
          <button class="btn btn-primary retry-btn">Try Again</button>
        </div>
      `;
    }

    if (!this.posts || this.posts.length === 0) {
      let emptyMessage = 'Be the first to share something with your friends!';
      if (this.isGroupMode()) {
        emptyMessage = this.threadId
          ? 'Be the first to post in this thread!'
          : 'Be the first to post in this group!';
      }
      return `
        <div class="empty-state">
          <div class="empty-state-icon">📝</div>
          <h3>No posts yet</h3>
          <p>${emptyMessage}</p>
        </div>
      `;
    }

    const groupAttr = this.isGroupMode() ? ` group-id="${this.groupId}"` : '';
    return `
      <div class="posts-container">
        ${this.posts.map((post, index) => {
    const itemGroupAttr = this.isGroupMode()
      ? groupAttr
      : (post.group_id ? ` group-id="${post.group_id}"` : '');
    return `
          <post-item data-post-index="${index}"${itemGroupAttr}></post-item>
        `;
  }).join('')}
      </div>
      ${this.renderLoadMoreButton()}
    `;
  }

  renderLoadMoreButton() {
    const hasNext = this.pagination && this.pagination.hasNext;
    const hasPrevious = this.currentPage > 1;
    const showPagination = hasNext || hasPrevious;

    if (!showPagination) {
      return '';
    }

    if (this.isLoadingMore || this.isLoading) {
      return `
        <div class="load-more-container">
          <button class="load-more-btn loading" disabled>
            <span class="spinner-small"></span>
            Loading posts...
          </button>
        </div>
      `;
    }

    return `
      <div class="load-more-container">
        <div class="pagination-buttons">
          ${hasPrevious ? `
            <button class="load-previous-btn">
              <span class="load-more-arrow">⬆️</span>
              <span class="load-more-text">Previous Page</span>
            </button>
          ` : ''}
          ${hasNext ? `
            <button class="load-more-btn">
              <span class="load-more-icon">✨</span>
              <span class="load-more-text">Next Page</span>
              <span class="load-more-arrow">⬇️</span>
            </button>
          ` : ''}
        </div>
        <div class="pagination-info">
          Page ${this.currentPage}${this.pagination ? ` of ${this.pagination.totalPages || Math.ceil(this.pagination.total / 10)}` : ''} 
          (${this.posts.length}${this.pagination ? ` of ${this.pagination.total}` : ''} posts)
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Refresh button
    const refreshBtn = this.querySelector('.refresh-btn');
    refreshBtn?.addEventListener('click', () => {
      this.load();
    });

    const openFeedFiltersBtn = this.querySelector('#openFeedFiltersBtn');
    openFeedFiltersBtn?.addEventListener('click', () => {
      this.openFeedFiltersModal();
    });

    // Retry button (appears when there's an error)
    const retryBtn = this.querySelector('.retry-btn');
    retryBtn?.addEventListener('click', () => {
      this.load();
    });

    // Load More button
    const loadMoreBtn = this.querySelector('.load-more-btn');
    loadMoreBtn?.addEventListener('click', () => {
      this.loadMore();
    });

    // Load Previous button
    const loadPreviousBtn = this.querySelector('.load-previous-btn');
    loadPreviousBtn?.addEventListener('click', () => {
      this.loadPrevious();
    });
  }

  attachDocumentListeners() {
    // Only attach document-level listeners once
    if (this.documentListenersAttached) {
      return;
    }
    this.documentListenersAttached = true;

    // Listen for new posts
    document.addEventListener('post-created', (event) => {
      if (!this.isConnected) {
        return;
      }
      const post = event.detail;
      if (this.isGroupMode()) {
        const postGroupId = Number(post?.group_id);
        if (!postGroupId || postGroupId !== Number(this.groupId)) {
          return;
        }
        const postThreadId = post?.thread_id !== null && post?.thread_id !== undefined && post.thread_id !== ''
          ? Number(post.thread_id)
          : null;
        const feedThreadId = this.threadId !== null && this.threadId !== undefined ? Number(this.threadId) : null;
        if (postThreadId !== feedThreadId) {
          return;
        }
      } else if (post?.group_id) {
        const postThreadId = post?.thread_id !== null && post?.thread_id !== undefined && post.thread_id !== ''
          ? Number(post.thread_id)
          : null;
        if (postThreadId !== null) {
          return;
        }
      }
      this.handleNewPost(post);
    });

    // Listen for feed filter changes (home feed only)
    document.addEventListener('feed-filters-changed', (event) => {
      if (!this.isConnected || this.isGroupMode()) {
        return;
      }
      this.currentPage = 1;
      this.updatePageInUrl(1, true);
      this.load(true);
    });

    // Listen for deleted posts
    document.addEventListener('post-deleted', (event) => {
      this.handleDeletedPost(event.detail.postId);
    });
  }

  updatePageInUrl(page, replace = false) {
    const url = new URL(window.location.href);
    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    if (replace) {
      window.history.replaceState({ page }, '', nextUrl);
    } else {
      window.history.pushState({ page }, '', nextUrl);
    }
  }

  buildGroupPostsQuery() {
    const params = new URLSearchParams();
    params.set('page', String(this.currentPage));
    params.set('limit', '10');
    if (this.threadId) {
      params.set('thread_id', String(this.threadId));
    }
    return params.toString();
  }

  async load(reset = true) {
    // Don't load if component is not visible (unless forced)
    // Allow loading if we're already loaded (for refresh, pagination, etc.)
    if (!this.hasLoadedOnce && !isVisible(this)) {
      // Not visible and never loaded, wait for visibility
      this.loadIfVisible();
      return;
    }

    // Prevent multiple simultaneous calls
    if (this.isLoadingPosts) {
      return;
    }

    this.isLoadingPosts = true;

    if (reset) {
      this.isLoading = true;
      if (!new URLSearchParams(window.location.search).get('page')) {
        this.currentPage = 1;
      }
      this.posts = [];

      if (this.currentPage === 1) {
        this.updatePageInUrl(1, true);
      }
    } else {
      this.isLoadingMore = true;
    }

    // Ensure posts is always an array
    if (!this.posts) {
      this.posts = [];
    }

    this.error = null;
    this.updateContent();

    try {
      if (this.isGroupMode()) {
        const response = await window.API.request(`/groups/${this.groupId}/posts?${this.buildGroupPostsQuery()}`);
        this.posts = response.posts || [];
        this.pagination = response.pagination || null;
      } else {
        const response = await window.API.posts.getAll(this.currentPage, 10);
        this.posts = response.posts;
        this.pagination = response.pagination;
      }
      this.error = null;
    } catch (error) {
      window.logger.error('Failed to load posts', error, { component: 'posts-feed' });
      this.error = error.message || 'Failed to load posts';
      if (reset) {
        this.posts = [];
      }
    } finally {
      this.isLoading = false;
      this.isLoadingMore = false;
      this.isLoadingPosts = false;
      this.updateContent();
      if (this.isGroupMode()) {
        queueMicrotask(() => {
          focusGroupFeedHash({ root: this, feed: this }).catch((err) => {
            window.logger?.warn('Failed to focus group post from hash', err);
          });
        });
      }
    }
  }

  async loadMore() {
    if (this.pagination && this.pagination.hasNext && !this.isLoadingMore && !this.isLoadingPosts) {
      this.currentPage++;
      this.updatePageInUrl(this.currentPage);
      await this.load(true);
    }
  }

  async loadPrevious() {
    if (this.currentPage > 1 && !this.isLoadingMore && !this.isLoadingPosts) {
      this.currentPage--;
      this.updatePageInUrl(this.currentPage);
      await this.load(true);
    }
  }

  handleNewPost(newPost) {
    // Add the new post to the beginning of the array
    this.posts.unshift(newPost);
    this.updateContent();

    // Add fade-in animation to the new post
    setTimeout(() => {
      const firstPost = this.querySelector('post-item');
      if (firstPost) {
        firstPost.classList.add('fade-in');
      }
    }, 100);
  }

  handleDeletedPost(postId) {
    // Remove the post from the array
    this.posts = this.posts.filter(post => post.id !== parseInt(postId));
    this.updateContent();
  }

  updateContent() {
    const feedContent = this.querySelector('.feed-content');
    if (feedContent) {
      feedContent.innerHTML = this.renderContent();

      // Set post data via JavaScript properties instead of HTML attributes
      const postItems = feedContent.querySelectorAll('post-item');
      postItems.forEach((postItem) => {
        const postIndex = parseInt(postItem.dataset.postIndex);
        if (this.posts && this.posts[postIndex]) {
          postItem.setCurrentUser(this.currentUser);
          if (this.isGroupMode()) {
            postItem.setAttribute('group-id', String(this.groupId));
          } else if (this.posts[postIndex].group_id) {
            postItem.setAttribute('group-id', String(this.posts[postIndex].group_id));
          } else {
            postItem.removeAttribute('group-id');
          }
          postItem.postData = this.posts[postIndex];
        }
      });

      this.attachEventListeners();
    }
  }

  // Public method to refresh posts
  refresh() {
    this.load();
  }

  // Public method to add a post
  addPost(post) {
    this.handleNewPost(post);
  }

  isAuthenticated() {
    return this.currentUser !== null;
  }

}

// Register the custom element
customElements.define('posts-feed', PostsFeed);
